const API_URL = process.env.REACT_APP_API_URL;

const fetchSuggestions = async (query, mapCenter) => {
  const response = await fetch(`${API_URL}/api/v1/autosuggestions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: query,
      lat: mapCenter.lat,
      lng: mapCenter.lng,
    }),
  });
  const data = await response.json();
  return data;
};

const fetchSelectedSuggestion = async (placeId) => {
  const response = await fetch(
    `${API_URL}/api/v1/selected-autosuggestion-data`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        placeId: placeId,
      }),
    },
  );
  const data = await response.json();
  return data;
};

const searchPlaces = async (mapBounds, acceptsReservations, hasWineGlasses) => {
  const response = await fetch(`${API_URL}/api/v1/SearchPlaces`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      mapBounds: {
        north: mapBounds["north"],
        south: mapBounds["south"],
        east: mapBounds["east"],
        west: mapBounds["west"],
      },
      acceptsReservations: acceptsReservations,
      hasWineGlasses: hasWineGlasses,
    }),
  });
  const data = await response.json();
  return data;
};

const createPlace = async (
  placeId,
  corkageFee,
  acceptsReservations,
  hasWineGlasses,
  notes,
  bottleLimit,
) => {
  await fetch(`${API_URL}/api/v1/CreatePlace`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      placeId: placeId,
      corkageFee: corkageFee,
      acceptsReservations: acceptsReservations,
      hasWineGlasses: hasWineGlasses,
      notes: notes,
      bottleLimit: bottleLimit,
    }),
  });
};

const getPlace = async(placeId) => {
  const response = await fetch(`${API_URL}/api/v1/GetPlace`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      placeId: placeId,
    }),
  });
  const data = await response.json();
  return data;
}

const updatePlace = async (
  placeId,
  corkageFee,
  acceptsReservations,
  hasWineGlasses,
  notes,
  bottleLimit,
) => {
  await fetch(`${API_URL}/api/v1/UpdatePlace`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      placeId: placeId,
      corkageFee: corkageFee,
      acceptsReservations: acceptsReservations,
      hasWineGlasses: hasWineGlasses,
      notes: notes,
      bottleLimit: bottleLimit,
    }),
  });
};

const getProfile = async () => {
  const response = await fetch(`${API_URL}/api/v1/GetProfile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });
  const data = await response.json();
  return data;
};

const createProfile = async (
  { username, firstName, lastName, city, bio },
) => {
  const response = await fetch(`${API_URL}/api/v1/CreateProfile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      username: username,
      firstName: firstName,
      lastName: lastName,
      city: city,
      bio: bio,
    }),
  });
  const data = await response.json();
  return data;
};

const updateProfile = async ({ firstName, lastName, city, bio }) => {
  const response = await fetch(`${API_URL}/api/v1/UpdateProfile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      firstName,
      lastName,
      city,
      bio,
    }),
  });
  const data = await response.json();
  return data;
};

const usernameExists = async (username) => {
  const response = await fetch(`${API_URL}/api/v1/UsernameExists`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      username: username,
    }),
  });
  const data = await response.json();
  return data.available;
};

// Wine API Functions
const listWines = async () => {
  try {
    const response = await fetch(`${API_URL}/api/v1/ListWines`);
    if (!response.ok) {
      throw new Error('Failed to fetch wines');
    }
    return await response.json();
  } catch (error) {
    console.error('Error listing wines:', error);
    throw error;
  }
};

const createWine = async (wineData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/CreateWine`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(wineData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to add wine');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating wine:', error);
    throw error;
  }
};

const getWine = async (wineId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/GetWine`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ wineId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get wine');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting wine:', error);
    throw error;
  }
};

const updateWine = async (wineData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/UpdateWine`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(wineData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update wine');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating wine:', error);
    throw error;
  }
};

// Tasting Notes API Functions
const listTastingNotes = async () => {
  try {
    const response = await fetch(`${API_URL}/api/v1/ListTastingNotes`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tasting notes');
    }

    return await response.json();
  } catch (error) {
    console.error('Error listing tasting notes:', error);
    throw error;
  }
};

const createTastingNote = async (noteData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/CreateTastingNote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(noteData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to add tasting note');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating tasting note:', error);
    throw error;
  }
};

const getTastingNote = async (noteId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/GetTastingNote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ noteId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get tasting note');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting tasting note:', error);
    throw error;
  }
};

const updateTastingNote = async (noteData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/UpdateTastingNote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(noteData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update tasting note');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating tasting note:', error);
    throw error;
  }
};

const deleteTastingNote = async (noteId) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/DeleteTastingNote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ noteId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete tasting note');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting tasting note:', error);
    throw error;
  }
};

const importTastingNotesFromCellarTracker = async (csvData) => {
  try {
    const response = await fetch(`${API_URL}/api/v1/ImportTastingNotesFromCellarTracker`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ csvData }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to import tasting notes');
    }

    return await response.json();
  } catch (error) {
    console.error('Error importing tasting notes:', error);
    throw error;
  }
};

const importTastingNotesFile = async (file) => {
  try {
    // Create FormData object to hold the file
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/v1/ImportTastingNotesFile`, {
      method: 'POST',
      credentials: 'include',
      // Don't set Content-Type header - browser will set correct boundary for multipart/form-data
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to import tasting notes');
    }

    return await response.json();
  } catch (error) {
    console.error('Error importing tasting notes:', error);
    throw error;
  }
};

const apiService = {
  fetchSuggestions,
  fetchSelectedSuggestion,
  searchPlaces,
  createPlace,
  updatePlace,
  getPlace,
  getProfile,
  createProfile,
  updateProfile,
  usernameExists,
  listWines,
  createWine,
  getWine,
  updateWine,
  listTastingNotes,
  createTastingNote,
  getTastingNote,
  updateTastingNote,
  deleteTastingNote,
  importTastingNotesFromCellarTracker,
  importTastingNotesFile,
};

export default apiService;
